import { Button } from "@shared/ui";
import { stubAccount, stubPolicy } from "../lib/axle";
import { Field, InlineError, inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import CarrierMark from "../components/CarrierMark";

// External dependencies
import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";

// Validation messages shown under their field rather than in the error box.
const FIELD_ERRORS = {
  "vehicle-year": ["Enter your vehicle year"],
  "vehicle-make": ["Enter your vehicle make"],
  "vehicle-model": ["Enter your vehicle model"],
};

const BasicPolicyInfo = ({
  nextStep,
  setAccountInfo,
  setPolicyInfo,
  loginInformation,
  session,
  posthog,
  step,
}) => {
  const ignitionToken = session.id;

  const [vehicleYear, setVehicleYear] = useState(1900);
  const [vehicleMake, setVehicleMake] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!vehicleYear) {
      setError(FIELD_ERRORS["vehicle-year"][0]);
      return;
    }

    if (!vehicleMake) {
      setError(FIELD_ERRORS["vehicle-make"][0]);
      return;
    }

    if (!vehicleModel) {
      setError(FIELD_ERRORS["vehicle-model"][0]);
      return;
    }

    const processDetail = "basic";

    //TODO: remove once we validate which pathway doesn't set ignitionToken (refer to AXL-4235)
    if (isEmpty(ignitionToken)) {
      console.log(`processDetail: ${processDetail}`);
      console.error("No ignitionToken provided"); //Use this .error to search in Sentry
    }

    // Save account stub
    const [account, accountError] = await stubAccount(
      ignitionToken,
      {
        carrier: get(loginInformation, "carrier.id"),
      },
      processDetail
    );

    if (accountError) {
      switch (get(accountError, "code")) {
        case 500:
          console.log(JSON.stringify(accountError));
          nextStep("failed");
          setError(accountError.message);
          return;
        default:
          nextStep("connection-error");
          return;
      }
    }

    // Save policy stub

    const [stubPolicyResponse, stubPolicyError] = await stubPolicy(
      ignitionToken,
      {
        account: account.id,
        carrier: get(loginInformation, "carrier.id"),
        vehicleModel,
        vehicleYear,
        vehicleMake,
      },
      processDetail
    );

    if (stubPolicyError) {
      switch (get(stubPolicyError, "code")) {
        case 500:
          console.log(JSON.stringify(stubPolicyError));
          nextStep("failed");
          setError(stubPolicyError.message);
          return;
        default:
          nextStep("connection-error");
          return;
      }
    }

    // Set to state
    setAccountInfo({ id: account.id });
    setPolicyInfo({ id: stubPolicyResponse.id });

    // If require backup docs, go to manual_policy_docs, else go to success
    get(session, "config.basic.documents")
      ? nextStep("manual-policy-document")
      : nextStep("success");
  };

  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog, step]);

  const fieldError = Object.keys(FIELD_ERRORS).find((id) =>
    FIELD_ERRORS[id].includes(error)
  );

  return (
    <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
      <StepBody>
        <CarrierMark loginInformation={loginInformation} />
        <StepHeading
          title="Enter your vehicle information"
          description={
            <>
              Axle will only share the{" "}
              <span className="font-medium text-ink">
                {get(loginInformation, "carrier.name")}
              </span>{" "}
              policy associated with this vehicle.
            </>
          }
        />
        <div className="flex flex-col gap-4">
          {error && !fieldError && (
            <div
              className="rounded border border-danger-border p-3"
              role="status"
            >
              <InlineError>{error}</InlineError>
            </div>
          )}
          <Field
            id="vehicle-year"
            label="Vehicle year"
            error={fieldError === "vehicle-year" ? error : undefined}
          >
            <input
              id="vehicle-year"
              aria-invalid={fieldError === "vehicle-year"}
              aria-describedby={fieldError === "vehicle-year" ? "vehicle-year-error" : undefined}
              placeholder="e.g. 2021"
              value={vehicleYear}
              className={inputClassName}
              type="number"
              onChange={(e) => setVehicleYear(e.target.value)}
            />
          </Field>
          <Field
            id="vehicle-make"
            label="Vehicle make"
            error={fieldError === "vehicle-make" ? error : undefined}
          >
            <input
              id="vehicle-make"
              aria-invalid={fieldError === "vehicle-make"}
              aria-describedby={fieldError === "vehicle-make" ? "vehicle-make-error" : undefined}
              placeholder="e.g. Toyota"
              value={vehicleMake}
              className={inputClassName}
              type="text"
              onChange={(e) => setVehicleMake(e.target.value)}
            />
          </Field>
          <Field
            id="vehicle-model"
            label="Vehicle model"
            error={fieldError === "vehicle-model" ? error : undefined}
          >
            <input
              id="vehicle-model"
              aria-invalid={fieldError === "vehicle-model"}
              aria-describedby={fieldError === "vehicle-model" ? "vehicle-model-error" : undefined}
              placeholder="e.g. Camry"
              value={vehicleModel}
              className={inputClassName}
              type="text"
              onChange={(e) => setVehicleModel(e.target.value)}
            />
          </Field>
        </div>
      </StepBody>
      <StepFooter>
        <Button type={"submit"}>Continue</Button>
      </StepFooter>
    </form>
  );
};

export default BasicPolicyInfo;
